// Simple session logger - stores current state, not event history
// This is much simpler and more reliable

import { supabase } from '../utils/supabase';
import * as SessionStateService from './sessionStateService';
import {
  deriveConsistentWaitingList,
  findCrossContextPlayerDuplicates,
} from '../utils/sessionMatchModel';
import { persistDebugLog, sessionPersistDebugSummary } from '../utils/sessionPersistDebug';

class SimpleSessionLogger {
  constructor(sessionId, options = {}) {
    this.sessionId = sessionId;
    this.readOnly = options.readOnly === true; // When true (e.g. non-host viewer), skip all writes to avoid 403
    this.useNormalizedState = null; // Cache the feature flag
    this.isSocialSessionCache = null; // Cache social session check
    this.saveInProgress = false;
    this.pendingSave = null;
  }

  /** True while a save (or queued follow-up save) is in flight — used to skip Realtime refetch on host during writes */
  get isSaving() {
    return this.saveInProgress === true || this.pendingSave != null;
  }

  /**
   * Check if this session should use normalized state
   * Caches the result to avoid repeated database calls
   */
  async shouldUseNormalizedState() {
    if (this.useNormalizedState !== null) {
      return this.useNormalizedState;
    }

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('use_normalized_state')
        .eq('id', this.sessionId)
        .single();

      if (error) {
        console.error('❌ Error checking normalized state flag:', error);
        this.useNormalizedState = false; // Default to old system
        return false;
      }

      this.useNormalizedState = data?.use_normalized_state === true;
      persistDebugLog(this.sessionId, 'normalized_flag', { useNormalizedState: this.useNormalizedState });
      return this.useNormalizedState;
    } catch (error) {
      console.error('❌ Error checking normalized state flag:', error);
      this.useNormalizedState = false;
      return false;
    }
  }

  /**
   * Check if this session is a social session
   * Caches the result to avoid repeated database calls
   */
  async isSocialSession() {
    if (this.isSocialSessionCache !== null) {
      return this.isSocialSessionCache;
    }

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          session_types:session_type_id (
            name
          ),
          is_public
        `)
        .eq('id', this.sessionId)
        .single();

      if (error) {
        console.error('❌ Error checking session type:', error);
        this.isSocialSessionCache = false; // Default to competitive
        return false;
      }

      const isSocial = data?.session_types?.name === 'Social Session' || data?.is_public === true;
      this.isSocialSessionCache = isSocial;
      return isSocial;
    } catch (error) {
      console.error('❌ Error checking session type:', error);
      this.isSocialSessionCache = false;
      return false;
    }
  }

  // Save current state to database
  async saveCurrentState(nextAssignments, waitingList, courtAssignments, playerInteractions = {}, matchmakingMode = 'STANDARD', deferredPlayers = new Map(), activeOpportunities = [], playerWaitMetrics = {}) {
    if (this.readOnly) return;
    if (this.saveInProgress) {
      // Queue latest state and return; avoids overlapping writes and DB deadlocks
      this.pendingSave = {
        nextAssignments,
        waitingList,
        courtAssignments,
        playerInteractions,
        matchmakingMode,
        deferredPlayers,
        activeOpportunities,
        playerWaitMetrics
      };
      return;
    }

    this.saveInProgress = true;
    try {
      persistDebugLog(
        this.sessionId,
        'save_start',
        sessionPersistDebugSummary(this.sessionId, {
          nextAssignments,
          waitingList,
          courtAssignments,
        })
      );
      
      // VALIDATION: duplicates within each context, then across contexts (wait / active / next_up)
      
      // Check waiting list for duplicates
      const uniqueWaitingList = [...new Set(waitingList || [])];
      if ((waitingList || []).length !== uniqueWaitingList.length) {
        console.error('🚨 VALIDATION ERROR: Duplicate players in waiting list!', {
          waitingList: waitingList,
          uniqueWaitingList: uniqueWaitingList,
          duplicates: (waitingList || []).length - uniqueWaitingList.length
        });
        throw new Error(`Validation failed: ${(waitingList || []).length - uniqueWaitingList.length} duplicate players detected in the queue.`);
      }
      
      // Check nextAssignments for duplicates within each match
      if (nextAssignments && nextAssignments.length > 0) {
        for (let i = 0; i < nextAssignments.length; i++) {
          const match = nextAssignments[i];
          const allMatchPlayers = [...(match.team1 || []), ...(match.team2 || [])];
          const uniqueMatchPlayers = [...new Set(allMatchPlayers)];
          if (allMatchPlayers.length !== uniqueMatchPlayers.length) {
            console.error('🚨 VALIDATION ERROR: Duplicate players in upcoming match!', {
              matchIndex: i,
              match: match,
              allMatchPlayers: allMatchPlayers,
              uniqueMatchPlayers: uniqueMatchPlayers,
              duplicates: allMatchPlayers.length - uniqueMatchPlayers.length
            });
            throw new Error(`Validation failed: ${allMatchPlayers.length - uniqueMatchPlayers.length} duplicate players detected in upcoming match ${i}.`);
          }
        }
      }
      
      // Check courtAssignments for duplicates within each court
      if (courtAssignments && courtAssignments.length > 0) {
        for (let i = 0; i < courtAssignments.length; i++) {
          const court = courtAssignments[i];
          const allCourtPlayers = [...(court.team1 || []), ...(court.team2 || [])];
          const uniqueCourtPlayers = [...new Set(allCourtPlayers)];
          if (allCourtPlayers.length !== uniqueCourtPlayers.length) {
            console.error('🚨 VALIDATION ERROR: Duplicate players in court assignment!', {
              courtIndex: i,
              court: court,
              allCourtPlayers: allCourtPlayers,
              uniqueCourtPlayers: uniqueCourtPlayers,
              duplicates: allCourtPlayers.length - uniqueCourtPlayers.length
            });
            throw new Error(`Validation failed: ${allCourtPlayers.length - uniqueCourtPlayers.length} duplicate players detected in court ${i}.`);
          }
        }
      }

      const { waitingList: coalescedWaitingList, removedBecausePlacedElsewhere } =
        deriveConsistentWaitingList(waitingList, courtAssignments, nextAssignments);
      if (removedBecausePlacedElsewhere.length > 0) {
        console.warn(
          'SimpleSessionLogger: Dropped',
          removedBecausePlacedElsewhere.length,
          'player(s) from waitlist snapshot (already on active or upcoming court).',
          removedBecausePlacedElsewhere.slice(0, 6)
        );
      }

      // After coalescing waitlist, any duplicate is a real bug (e.g. same player on two courts).
      const cross = findCrossContextPlayerDuplicates(
        coalescedWaitingList,
        courtAssignments,
        nextAssignments
      );
      if (cross.duplicateIds.length > 0) {
        const sample = cross.duplicateIds.slice(0, 8);
        const detail = sample.map((id) => ({ id, where: cross.contexts.get(id) }));
        console.error('🚨 VALIDATION ERROR: Inconsistent placement (same player in two courts or court+upcoming)', detail);
        throw new Error(
          `Validation failed: ${cross.duplicateIds.length} player(s) have conflicting placements (e.g. ${sample.join(', ')}).`
        );
      }

      // Check if we should use normalized state
      const useNormalized = await this.shouldUseNormalizedState();

      if (useNormalized) {
        // Use new normalized tables - save sequentially to avoid deadlocks
        persistDebugLog(this.sessionId, 'normalized_save', { step: 'start' });
        
        // IMPORTANT: Run sequentially, not in parallel!
        // Parallel updates can cause deadlocks when multiple operations try to lock
        // the same session_players rows in different orders
        await SessionStateService.updateWaitingList(this.sessionId, coalescedWaitingList || []);
        // Check if this is a social session (skip court assignments entirely - no court management)
        const isSocialSession = await this.isSocialSession();
        if (!isSocialSession) {
          await SessionStateService.updateCourtAssignments(this.sessionId, courtAssignments || [], 'active', false);
          await SessionStateService.updateCourtAssignments(this.sessionId, nextAssignments || [], 'next_up', false);
        }
        
        // Note: playerStreaks removed - calculated on-the-fly from session_games
        
        // ⚠️ SKIP wait metrics in debounced save!
        // Wait metrics are updated immediately via resetCourtsWatched/incrementCourtsWatched
        // The local React state is often stale and would overwrite the correct DB values
        persistDebugLog(this.sessionId, 'normalized_save', { step: 'skip_wait_metrics' });
        // await SessionStateService.updatePlayerWaitMetrics(this.sessionId, playerWaitMetrics || {});

        persistDebugLog(this.sessionId, 'normalized_save', { step: 'done' });
      } else {
        // Use old JSONB system
        console.log('💾 SimpleSessionLogger: Using JSONB state (old system)');
      
        const { data, error } = await supabase
          .from('session_state')
          .upsert({
            session_id: this.sessionId,
            next_assignments: nextAssignments || [],
            waiting_list: coalescedWaitingList || [],
            court_assignments: courtAssignments || [],
            // player_streaks removed - calculated on-the-fly from session_games
            player_interactions: playerInteractions instanceof Map ? Object.fromEntries(playerInteractions) : (playerInteractions || {}),
            matchmaking_mode: matchmakingMode || 'STANDARD',
            deferred_players: Object.fromEntries(deferredPlayers || new Map()),
            active_opportunities: activeOpportunities || [],
            player_wait_metrics: playerWaitMetrics || {},
            updated_at: new Date().toISOString()
          });
          
        if (error) {
          console.error('❌ Supabase upsert error:', error);
          throw error;
        }
        console.log('✅ SimpleSessionLogger: Current state saved to JSONB for session_id:', this.sessionId);
      }
    } catch (err) {
      console.error('❌ SimpleSessionLogger: Failed to save current state for session_id:', this.sessionId, 'error:', err);
      throw err; // Re-throw to let the calling code handle the error
    } finally {
      this.saveInProgress = false;
      if (this.pendingSave) {
        const queued = this.pendingSave;
        this.pendingSave = null;
        await this.saveCurrentState(
          queued.nextAssignments,
          queued.waitingList,
          queued.courtAssignments,
          queued.playerInteractions,
          queued.matchmakingMode,
          queued.deferredPlayers,
          queued.activeOpportunities,
          queued.playerWaitMetrics
        );
      }
    }
  }

  /**
   * Drop any queued (not yet executing) pending save.
   * Call this before an authoritative DB reload so a stale pre-RPC snapshot
   * queued as pendingSave cannot fire after the reload and overwrite correct data.
   * The currently in-flight save (saveInProgress=true) is unaffected — it already
   * started writing and its data predates the RPC, which is fine because the RPC
   * runs after it and corrects the DB; the reload then reads the RPC-corrected state.
   */
  cancelPendingSave() {
    this.pendingSave = null;
  }

  // Load current state from database
  async loadCurrentState() {
    try {
      console.log('🔍 SimpleSessionLogger: Loading state for session_id:', this.sessionId);
      
      // Check authentication first
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError) {
        console.error('🔍 SimpleSessionLogger: Auth error:', authError);
        return null;
      }
      if (!session) {
        console.error('🔍 SimpleSessionLogger: No active session found');
        return null;
      }
      console.log('🔍 SimpleSessionLogger: Auth session found for user:', session.user.id);
      
      // Check if we should use normalized state
      const useNormalized = await this.shouldUseNormalizedState();
      
      if (useNormalized) {
        // Use new normalized tables
        console.log('🔍 SimpleSessionLogger: Using NORMALIZED state (new system)');
        const state = await SessionStateService.loadNormalizedState(this.sessionId);
        return state ? {
          session_id: this.sessionId,
          next_assignments: state.nextAssignments || [],
          waiting_list: state.waitingList || [],
          orphaned_waitlist_player_ids: state.orphanedWaitlistPlayerIds || [],
          court_assignments: state.courtAssignments || [],
          maxCourtNumber: state.maxCourtNumber || 0, // Max court number from DB (handles empty courts)
          upcoming_queue_slot_count: state.upcomingQueueSlotCount ?? 1,
          // player_streaks removed - calculated on-the-fly from session_games
          player_interactions: state.playerInteractions || {},
          player_wait_metrics: state.playerWaitMetrics || {},
          matchmaking_mode: 'TWO_STAGE', // Default for new system
          deferred_players: {},
          active_opportunities: []
        } : null;
      } else {
        // Use old JSONB system
        console.log('🔍 SimpleSessionLogger: Using JSONB state (old system)');
      const { data, error } = await supabase
        .from('session_state')
        .select('*')
        .eq('session_id', this.sessionId)
        .single();
      
      console.log('🔍 SimpleSessionLogger: Database query result:', { data, error });

      if (error) {
        if (error.code === 'PGRST116') {
          // No row found - this is normal for new sessions, not an error
          console.log('🔍 SimpleSessionLogger: No saved state found for session_id:', this.sessionId, '(normal for new sessions)');
          return null;
        }
        // Only log as error if it's not the expected "no rows found" case
        console.error('🔍 SimpleSessionLogger: Database error:', error);
        throw error;
      }

      console.log('✅ SimpleSessionLogger: Loaded current state from database for session_id:', this.sessionId, 'data:', data);
      return data;
      }
    } catch (err) {
      console.error('❌ Failed to load current state:', err);
      return null;
    }
  }

  // Save just the next assignments
  async saveNextAssignments(nextAssignments) {
    if (this.readOnly) return;
    console.log('🔥 SimpleSessionLogger: saveNextAssignments called with:', nextAssignments);
    console.log('🔥 SimpleSessionLogger: sessionId:', this.sessionId);
    try {
      const useNormalized = await this.shouldUseNormalizedState();

      if (useNormalized) {
        // Check if this is a social session (skip court assignments entirely - no court management)
        const isSocialSession = await this.isSocialSession();
        if (!isSocialSession) {
          await SessionStateService.updateCourtAssignments(this.sessionId, nextAssignments || [], 'next_up', false);
          console.log('✅ Next assignments saved to NORMALIZED tables');
        } else {
          console.log('⏭️  Social session - skipping next assignments (no court management)');
        }
      } else {
      const { data, error } = await supabase
        .from('session_state')
        .upsert({
          session_id: this.sessionId,
          next_assignments: nextAssignments || [],
          updated_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }
      
        console.log('✅ Next assignments saved to JSONB:', data);
      }
    } catch (err) {
      console.error('❌ Failed to save next assignments:', err);
    }
  }

  // Save just the waiting list
  async saveWaitingList(waitingList) {
    if (this.readOnly) return;
    try {
      const useNormalized = await this.shouldUseNormalizedState();

      if (useNormalized) {
        await SessionStateService.updateWaitingList(this.sessionId, waitingList || []);
      } else {
      await supabase
        .from('session_state')
        .upsert({
          session_id: this.sessionId,
          waiting_list: waitingList || [],
          updated_at: new Date().toISOString()
        });
      }
      // Logging removed for cleaner console
    } catch (err) {
      console.error('❌ Failed to save waiting list:', err);
    }
  }

  // Save just the court assignments
  async saveCourtAssignments(courtAssignments) {
    if (this.readOnly) return;
    try {
      const useNormalized = await this.shouldUseNormalizedState();

      if (useNormalized) {
        // Check if this is a social session (skip court assignments entirely - no court management)
        const isSocialSession = await this.isSocialSession();
        if (!isSocialSession) {
          await SessionStateService.updateCourtAssignments(this.sessionId, courtAssignments || [], 'active', false);
          console.log('✅ Court assignments saved to NORMALIZED tables');
        } else {
          console.log('⏭️  Social session - skipping court assignments (no court management)');
        }
      } else {
      await supabase
        .from('session_state')
        .upsert({
          session_id: this.sessionId,
          court_assignments: courtAssignments || [],
          updated_at: new Date().toISOString()
        });
        console.log('✅ Court assignments saved to JSONB');
      }
    } catch (err) {
      console.error('❌ Failed to save court assignments:', err);
    }
  }
}

export default SimpleSessionLogger;
